import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery, ApolloError } from "@apollo/client";
import { DateTime } from "luxon";
import { defineMessages, useIntl, IntlShape } from "react-intl";
import { Button } from "@natera/form";
import SpinnerLoader from "@app/neva/components/spinnerLoader/spinnerLoader";
import { TestHistoryContext, ErrorProvider } from "@app/provider";
import { ResultCodes } from "@app/service/resultCodes";
import { TimezoneSelector } from "@app/components/ui/inputs/timezoneSelector";
import { useErrorController } from "@natera/platform/lib/hooks";
import { TestDetailsRecord } from "@app/provider/types";
import { TestCardStatus, TestType } from "@app/provider/testData/types";
import TestCardService from "@app/service/testCard";
import {
  CounselingIntent,
  IntakeFormUserInputFields,
  classifyCounselingIntent,
} from "@app/service/intakeForm";
import {
  GIS_ENTRY_SOURCE_ORDER_LOOKUP,
  GIS_ENTRY_SOURCE_PUBLIC,
  GIS_NO_ORDER_UID,
  getGisEntrySource,
} from "@app/utils/gisEntrySource";
import { useGisIntakeFormData } from "@app/hooks/useGisIntakeFormData";
import { useEmbeddedFrameBack } from "@app/hooks/useEmbeddedFrameBack";
import {
  useAcuityAvailableDates,
  useAcuityAvailableTimes,
  useCreateAcuityAppointment,
  useAcuityCalendars,
  AcuityAppointment,
} from "@app/service/scheduling";
import { routes } from "@app/routing";
import Svg from "@natera/material/lib/svg";
import Checkmark from "@assets/svg/icons/checkmark.svg";
import CheckmarkSuccess from "@assets/svg/icons/checkmark_success.svg";
import ErrorIcon from "@assets/svg/icons/error.svg";
import CloseIcon from "@assets/svg/icons/close.svg";
import Bolt from "@assets/svg/bolt.svg";
import GoogleCalendarLogo from "@assets/images/google_calendar.png";
import AppleLogo from "@assets/svg/apple-logo.svg";
import { getTimeZones, getTranslatedTimeZoneUserValue } from "@app/utils";
import { getMonthsMap } from "@app/components/dob/months";
import { COUNTRIES, LANGUAGES } from "@app/utils/intakeFormOptions";
import { useIntakeForm, useGisGuest } from "./context";
import { useGisSessionOps } from "./useGisSessionOps";
import {
  FIELD_IDS,
  resolveCancerTypeAcuityValue,
  CANCER_STAGE_LABELS,
  REASON_LABELS,
  ACTIVE_DISEASE_LABELS,
  INTERPRETER_NEEDED_LABELS,
  COUNSELING_INTENT_LABELS,
  getAcuityOwnerConfig,
  getAcuityFlow,
  getAcuityFieldIdsForFlow,
  buildAcuitySchedulingUrl,
  getAcuityAppointmentConfig,
  resolveAcuityLookupType,
} from "./buildAcuityUrl";

import "./scheduleSessionPage.scss";

const messages = defineMessages({
  selectCounselorTitle: {
    id: "scheduleSession.selectCounselorTitle",
    defaultMessage: "Select a counselor",
  },
  appointmentLabel: {
    id: "scheduleSession.appointmentLabel",
    defaultMessage: "Appointment",
  },
  anyAvailable: {
    id: "scheduleSession.anyAvailable",
    defaultMessage: "Any Available",
  },
  earliestAvailability: {
    id: "scheduleSession.earliestAvailability",
    defaultMessage: "Earliest availability",
  },
  appointmentDurationShort: {
    id: "scheduleSession.appointmentDurationShort",
    defaultMessage: "30 mins",
  },
  nextButton: {
    id: "scheduleSession.nextButton",
    defaultMessage: "Next",
  },
  title: {
    id: "scheduleSession.title",
    defaultMessage: "Select date and time to schedule your session",
  },
  whSchedulerTitle: {
    id: "scheduleSession.whSchedulerTitle",
    defaultMessage: "Schedule your Empower session",
  },
  whSchedulerIframeLabel: {
    id: "scheduleSession.whSchedulerIframeLabel",
    defaultMessage: "Acuity Scheduling – Empower Genetic Information Session",
  },
  noSessions: {
    id: "scheduleSession.noSessions",
    defaultMessage: "No available sessions. Select a different date.",
  },
  scheduleButton: {
    id: "scheduleSession.scheduleButton",
    defaultMessage: "Schedule",
  },
  backButton: {
    id: "scheduleSession.backButton",
    defaultMessage: "Back",
  },
  confirmationTitle: {
    id: "scheduleSession.confirmationTitle",
    defaultMessage: "Your session is all set!",
  },
  confirmationSubtitle: {
    id: "scheduleSession.confirmationSubtitle",
    defaultMessage:
      "You will receive a confirmation email with all the details shortly.",
  },
  summaryTitle: {
    id: "scheduleSession.summaryTitle",
    defaultMessage: "Appointment summary",
  },
  dateLabel: {
    id: "scheduleSession.dateLabel",
    defaultMessage: "Date",
  },
  timeLabel: {
    id: "scheduleSession.timeLabel",
    defaultMessage: "Time",
  },
  timezoneLabel: {
    id: "scheduleSession.timezoneLabel",
    defaultMessage: "Timezone",
  },
  durationLabel: {
    id: "scheduleSession.durationLabel",
    defaultMessage: "Duration",
  },
  durationValue: {
    id: "scheduleSession.durationValue",
    defaultMessage: "30 minutes",
  },
  addToCalendar: {
    id: "scheduleSession.addToCalendar",
    defaultMessage: "Add to calendar",
  },
  google: {
    id: "scheduleSession.google",
    defaultMessage: "Google",
  },
  apple: {
    id: "scheduleSession.apple",
    defaultMessage: "Apple",
  },
  expectTitle: {
    id: "scheduleSession.expectTitle",
    defaultMessage: "What to expect",
  },
  expectBody: {
    id: "scheduleSession.expectBody",
    defaultMessage:
      "Genetic Information Sessions (GIS) are pre-test and post-test patient sessions to discuss Natera tests and results. For questions beyond the specific details of Natera's tests and test results, please reach out to your healthcare provider who may consider referring you to a genetic counselor or clinical geneticist for comprehensive genetic counseling.",
  },
  goToResults: {
    id: "scheduleSession.goToResults",
    defaultMessage: "Go to My Results",
  },
  slotUnavailable: {
    id: "scheduleSession.slotUnavailable",
    defaultMessage:
      "This time is no longer available. Please select another time.",
  },
  scheduleError: {
    id: "scheduleSession.scheduleError",
    defaultMessage:
      "We couldn't schedule your session. Please try again or select another time.",
  },
});

/** Short weekday labels in Sunday-first order (matches JavaScript Date#getDay()). */
const weekdayShortMessages = defineMessages({
  sun: { id: "weekday.sun", defaultMessage: "Sun" },
  mon: { id: "weekday.mon", defaultMessage: "Mon" },
  tue: { id: "weekday.tue", defaultMessage: "Tue" },
  wed: { id: "weekday.wed", defaultMessage: "Wed" },
  thu: { id: "weekday.thu", defaultMessage: "Thu" },
  fri: { id: "weekday.fri", defaultMessage: "Fri" },
  sat: { id: "weekday.sat", defaultMessage: "Sat" },
});

const SUNDAY_FIRST_WEEKDAY_ORDER = [
  weekdayShortMessages.sun,
  weekdayShortMessages.mon,
  weekdayShortMessages.tue,
  weekdayShortMessages.wed,
  weekdayShortMessages.thu,
  weekdayShortMessages.fri,
  weekdayShortMessages.sat,
] as const;

function getSundayFirstWeekdayShortLabels(intl: IntlShape): string[] {
  return SUNDAY_FIRST_WEEKDAY_ORDER.map((m) => intl.formatMessage(m));
}

interface CalendarDay {
  date: Date;
  dayOfMonth: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isAvailable: boolean;
}

function buildCalendarDays(
  year: number,
  month: number,
  availableDates: Set<string>
): CalendarDay[] {
  const today = new Date();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = firstDay.getDay();

  const days: CalendarDay[] = [];

  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month - 1, prevMonthLastDay - i);
    days.push({
      date: d,
      dayOfMonth: prevMonthLastDay - i,
      isCurrentMonth: false,
      isToday: false,
      isAvailable: false,
    });
  }

  for (let day = 1; day <= lastDay.getDate(); day++) {
    const d = new Date(year, month, day);
    const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    days.push({
      date: d,
      dayOfMonth: day,
      isCurrentMonth: true,
      isToday:
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate(),
      isAvailable: availableDates.has(dateKey),
    });
  }

  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    const d = new Date(year, month + 1, i);
    days.push({
      date: d,
      dayOfMonth: i,
      isCurrentMonth: false,
      isToday: false,
      isAvailable: false,
    });
  }

  return days;
}

function formatTimeFromISO(isoTime: string, tz: string): string {
  const dt = DateTime.fromISO(isoTime).setZone(tz);
  return dt.toFormat("h:mm a");
}

function formatAppointmentDateInZone(
  isoTime: string,
  tz: string,
  intl: IntlShape
): string {
  const instant = DateTime.fromISO(isoTime).toJSDate();
  return intl.formatDate(instant, {
    timeZone: tz,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getTimezoneOffsetLabel(tz: string): string {
  const now = DateTime.now().setZone(tz);
  return `GMT${now.toFormat("ZZ")}`;
}

type ScheduleView = "counselorSelection" | "dateSelection" | "confirmation";

export default function ScheduleSessionPage() {
  const intl = useIntl();
  const monthOptions = useMemo(() => getMonthsMap(intl), [intl]);
  const weekdayLabels = useMemo(
    () => getSundayFirstWeekdayShortLabels(intl),
    [intl]
  );
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();

  // Reached when the patient has no eligible test order (see PublicGisTriage's
  // no-order redirect). There's no backing order to scope the test-card query
  // to, so it's skipped; the order-scoped intake query is served from the
  // patient's profile (the sentinel uids resolve to a no-order intake on the
  // server). Scheduling defaults to the generic ONC Genetic Information
  // Session.
  const isNoOrder = orderUidPublic === GIS_NO_ORDER_UID;

  // Anonymous order-lookup (Public_Entry_Order_Lookup) guest. When a token is
  // present every GraphQL call routes through the NonAuth resolvers, the
  // authenticated test-history / test-card lookups are skipped, and the
  // matched order's test type / status (carried on the guest context) stand in
  // for the authenticated test card.
  const {
    token: guestToken,
    testType: guestTestType,
    status: guestStatus,
    counselingIntent: guestCounselingIntent,
    limsCaseId: guestLimsCaseId,
  } = useGisGuest();
  const isGuest = Boolean(guestToken);

  const {
    testCards,
    getTestHistory,
    isLoading: testHistoryIsLoading,
  } = useContext(TestHistoryContext);

  const { data } = useQuery<{ getTestCardDetails: TestDetailsRecord }>(
    TestCardService.getTestCardDetails(),
    {
      variables: { orderUidPublic, testUid },
      fetchPolicy: "cache-first",
      skip: isNoOrder || isGuest,
    }
  );
  const latestTest = data?.getTestCardDetails ?? null;
  const limsCaseId = isGuest ? guestLimsCaseId : latestTest?.limsId;

  // Authenticated test card backing the session (null for guests, who have no
  // authenticated test history). The matched-order status / test type carried
  // on the guest context stand in for it below.
  const testToUse = isGuest
    ? null
    : latestTest || (testCards && testCards.length > 0 ? testCards[0] : null);

  // Effective status / test type used for counseling-intent and Acuity routing,
  // sourced from the guest context for guests and the test card otherwise.
  const effectiveStatus = isGuest
    ? (guestStatus ?? undefined)
    : testToUse?.status;
  const effectiveTestTypeRaw = isGuest
    ? (guestTestType ?? undefined)
    : testToUse?.testType;

  const hasFetchedTestHistory = useRef(false);

  useEffect(() => {
    if (
      !isGuest &&
      !isNoOrder &&
      !latestTest &&
      !testCards?.length &&
      !hasFetchedTestHistory.current
    ) {
      hasFetchedTestHistory.current = true;
      getTestHistory();
    }
  }, [isGuest, isNoOrder, latestTest, testCards?.length, getTestHistory]);

  // The sentinel uids are passed through for no-order so the backend returns
  // profile-based prefill (name + contact) used to populate the Acuity
  // appointment. Guests fetch the same prefill via the token-authorized
  // NonAuth resolver.
  const {
    intakeFormData,
    isLoading: intakeFormLoading,
    error: intakeFormError,
  } = useGisIntakeFormData(
    orderUidPublic ?? null,
    testUid ?? null,
    guestToken ?? null
  );

  const {
    prefilledData,
    confirmedPhone,
    confirmedEmail,
    userInputFields,
    triageGisFlowType,
    counselingIntent,
    bookingConfirmed,
    setBookingConfirmed,
  } = useIntakeForm();
  // Scheduling Empower (whether picked at the select-test step or routed there
  // by triage) does not change the intent: it stays the one classified from the
  // order the intake was started for, so a post-test oncology patient books the
  // post-test Empower session and a pre-test one the pre-test session. Patients
  // with no order to classify are pre-test.
  const effectiveCounselingIntent: CounselingIntent =
    counselingIntent ??
    guestCounselingIntent ??
    classifyCounselingIntent(effectiveStatus);

  // The triage rules engine can override the test-type-based routing when it
  // returns WH_EMPOWER (e.g. an oncology test type, or a no-order patient who
  // only wants to discuss Empower, that triages to Women's Health Empower
  // scheduling). All Acuity resolution below must use this effective type, not
  // the raw test type, otherwise WH-routed patients still get booked into the
  // ONC owner/appointment type. See buildAcuityUrl.ts for the matching logic
  // on the URL-building path.
  //
  // The triage decision wins regardless of how the patient entered the flow:
  // a no-order patient who picked Signatera at the select-test step but chose
  // an Empower-only reason still triages to WH_EMPOWER and must land on the
  // Empower scheduler. When triage did not route to WH_EMPOWER, no-order
  // patients fall back to the test they picked at the select-test step
  // (persisted on `prefilledData.testType`): Empower → WH/Empower scheduler,
  // everything else → ONC, defaulting to SIGNATERA (ONC) when the selection is
  // missing (e.g. a deep link that skipped the select-test step).
  const effectiveTestType =
    triageGisFlowType === "WH_EMPOWER"
      ? TestType.EMPOWER
      : isNoOrder
        ? ((prefilledData?.testType as TestType | undefined) ??
          TestType.SIGNATERA)
        : // Guests have no authenticated test card (`testToUse` is null); their
          // matched-order test type lives on the guest context, surfaced via
          // `effectiveTestTypeRaw`. Use it so Acuity routing / appointment type
          // resolve for both guest and authenticated flows.
          ((effectiveTestTypeRaw as TestType | undefined) ?? undefined);

  // Intake steps can override server-provided values in context (for example,
  // clinic information collected from guests). Use those answers when
  // constructing either Acuity URL params or direct appointment fields.
  const acuityIntakeFormData = useMemo(() => {
    const base = intakeFormData ?? prefilledData;
    if (!base) return null;

    return {
      ...base,
      // A visitor we couldn't match to a patient record (no match, or an
      // account whose only order is too old to be eligible) has no name in the
      // server prefill — theirs is the one they typed in the lookup funnel and
      // confirmed on the intake form, which lives on `prefilledData`. The
      // booking's own name fields already prefer it.
      firstName: prefilledData?.firstName || base.firstName,
      lastName: prefilledData?.lastName || base.lastName,
      dateOfBirth: prefilledData?.dateOfBirth ?? base.dateOfBirth,
      physicianClinic: prefilledData?.physicianClinic ?? base.physicianClinic,
      orderingPhysician:
        prefilledData?.orderingPhysician ?? base.orderingPhysician,
      cancerType: prefilledData?.cancerType ?? base.cancerType,
      cancerStage: prefilledData?.cancerStage ?? base.cancerStage,
      testType: prefilledData?.testType ?? base.testType,
    };
  }, [intakeFormData, prefilledData]);

  // Pre-test orders have no result to view yet, so navigating to the test
  // result details page errors out. Only surface "Go to My Results" for
  // post-test orders. Mirrors the intent resolution used for the Acuity
  // counseling-intent field (intake context first, status classifier fallback).
  const isPostTest = effectiveCounselingIntent === "post-test";

  const { getActiveSession, updateStep, completeSession, confirmBooking } =
    useGisSessionOps();

  const errorController = useErrorController();
  const isLoading = testHistoryIsLoading || intakeFormLoading;

  const acuityAppointmentConfig = useMemo(() => {
    const acuityLookupType = resolveAcuityLookupType({
      testType: effectiveTestType,
      status: testToUse?.status,
      resultCode: testToUse?.resultCode,
    });

    return getAcuityAppointmentConfig(
      acuityLookupType,
      effectiveCounselingIntent,
      testToUse?.resultCode
    );
  }, [
    effectiveCounselingIntent,
    effectiveTestType,
    testToUse?.resultCode,
    testToUse?.status,
  ]);

  // Resolve Acuity appointment type ID from the test type, status, and result code.
  const appointmentTypeID =
    acuityAppointmentConfig?.acuityAppointmentTypeID ?? null;

  const appointmentTypeName =
    acuityAppointmentConfig?.acuityAppointmentType ??
    "Genetic Information Session";

  // The WH (Empower) flow uses Acuity's hosted scheduler in an iframe rather
  // than the custom Natera calendar/dates/times UI. We build the same Acuity
  // URL here that we'd use for an external link, and embed it.
  const isWhFlow = getAcuityFlow(effectiveTestType) === "WH";
  const whAcuityUrl = useMemo(() => {
    if (!isWhFlow) return null;
    return buildAcuitySchedulingUrl({
      latestTest: testToUse ?? null,
      caseId: limsCaseId,
      intakeFormData: acuityIntakeFormData,
      userInputFields,
      confirmedPhone,
      confirmedEmail,
      triageGisFlowType,
      // No-order WH (Empower) sessions have no `latestTest`, so the URL builder
      // can't infer the WH flow or classify an intent on its own — pass both
      // the resolved type and intent explicitly.
      testTypeOverride: isNoOrder ? effectiveTestType : undefined,
      counselingIntent: effectiveCounselingIntent,
    });
  }, [
    isWhFlow,
    testToUse,
    limsCaseId,
    acuityIntakeFormData,
    userInputFields,
    confirmedPhone,
    confirmedEmail,
    triageGisFlowType,
    isNoOrder,
    effectiveTestType,
    effectiveCounselingIntent,
  ]);

  // Acuity API hooks
  const {
    fetchDates,
    dates: availableDatesList,
    loading: datesLoading,
  } = useAcuityAvailableDates();
  const {
    fetchTimes,
    times: availableTimesList,
    loading: timesLoading,
  } = useAcuityAvailableTimes();
  const { createAppointment } = useCreateAcuityAppointment();
  const {
    fetchCalendars,
    calendars,
    loading: calendarsLoading,
  } = useAcuityCalendars();

  // View state
  const [view, setView] = useState<ScheduleView>("counselorSelection");
  const [selectedCalendarID, setSelectedCalendarID] = useState<
    number | null | undefined
  >(undefined);
  const [bookedAppointment, setBookedAppointment] =
    useState<AcuityAppointment | null>(null);
  const [isScheduleSubmitInFlight, setIsScheduleSubmitInFlight] =
    useState(false);
  const [scheduleError, setScheduleError] = useState(false);

  // Calendar state
  const now = new Date();
  const [calendarMonth, setCalendarMonth] = useState(now.getMonth());
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeISO, setSelectedTimeISO] = useState<string | null>(null);
  const [unavailableSlots, setUnavailableSlots] = useState<Set<string>>(
    new Set()
  );
  const [slotError, setSlotError] = useState(false);
  const slotErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (slotErrorTimerRef.current) clearTimeout(slotErrorTimerRef.current);
    };
  }, []);

  // Timezone state - default to browser timezone
  const browserTz = DateTime.local().zoneName;
  const timeZones = getTimeZones(intl);
  const defaultTz =
    timeZones.find((tz) => tz.browserValue === browserTz)?.browserValue ||
    "America/Los_Angeles";
  const [timezone, setTimezone] = useState(defaultTz);

  // Fetch calendars (counselors) for the appointment type.
  // Skipped on the WH flow — that flow uses Acuity's hosted iframe scheduler
  // and never renders the custom counselor/date/time UI driven by these hooks.
  useEffect(() => {
    if (isWhFlow) return;
    if (!appointmentTypeID || !orderUidPublic || !testUid) return;
    fetchCalendars({
      orderUidPublic,
      testUid,
      appointmentTypeID,
      timezone,
      token: guestToken,
    });
  }, [
    isWhFlow,
    appointmentTypeID,
    orderUidPublic,
    testUid,
    timezone,
    guestToken,
    fetchCalendars,
  ]);

  // Convert available dates from API to a Set for fast lookup
  const availableDates = useMemo(() => {
    const set = new Set<string>();
    for (const d of availableDatesList) {
      set.add(d.date);
    }
    return set;
  }, [availableDatesList]);

  // Fetch available dates whenever month/year/timezone/appointmentType/calendar changes
  useEffect(() => {
    if (isWhFlow) return;
    if (!appointmentTypeID || !orderUidPublic || !testUid) return;
    if (selectedCalendarID === undefined) return;
    const month = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}`;
    fetchDates({
      orderUidPublic,
      testUid,
      appointmentTypeID,
      month,
      timezone,
      calendarID: selectedCalendarID ?? undefined,
      token: guestToken,
    });
  }, [
    isWhFlow,
    calendarYear,
    calendarMonth,
    timezone,
    appointmentTypeID,
    orderUidPublic,
    testUid,
    selectedCalendarID,
    guestToken,
    fetchDates,
  ]);

  // Fetch available times when a date is selected
  useEffect(() => {
    if (isWhFlow) return;
    if (!selectedDate || !appointmentTypeID || !orderUidPublic || !testUid)
      return;
    const dateStr = DateTime.fromJSDate(selectedDate).toFormat("yyyy-MM-dd");
    fetchTimes({
      orderUidPublic,
      testUid,
      appointmentTypeID,
      date: dateStr,
      timezone,
      calendarID: selectedCalendarID ?? undefined,
      token: guestToken,
    });
    setSelectedTimeISO(null);
    setUnavailableSlots(new Set());
  }, [
    isWhFlow,
    selectedDate,
    timezone,
    appointmentTypeID,
    orderUidPublic,
    testUid,
    selectedCalendarID,
    guestToken,
    fetchTimes,
  ]);

  const calendarDays = useMemo(
    () => buildCalendarDays(calendarYear, calendarMonth, availableDates),
    [calendarYear, calendarMonth, availableDates]
  );

  const filteredTimesList = useMemo(
    () => availableTimesList.filter((slot) => !unavailableSlots.has(slot.time)),
    [availableTimesList, unavailableSlots]
  );

  const handlePrevMonth = useCallback(() => {
    setCalendarMonth((prev) => {
      if (prev === 0) {
        setCalendarYear((y) => y - 1);
        return 11;
      }
      return prev - 1;
    });
    setSelectedDate(null);
  }, []);

  const handleNextMonth = useCallback(() => {
    setCalendarMonth((prev) => {
      if (prev === 11) {
        setCalendarYear((y) => y + 1);
        return 0;
      }
      return prev + 1;
    });
    setSelectedDate(null);
  }, []);

  const handleMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setCalendarMonth(Number(e.target.value));
      setSelectedDate(null);
    },
    []
  );

  const handleYearChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setCalendarYear(Number(e.target.value));
      setSelectedDate(null);
    },
    []
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 3 }, (_, i) => currentYear + i);
  }, []);

  const handleDayClick = useCallback((day: CalendarDay) => {
    if (!day.isAvailable) return;
    setSelectedDate(day.date);
  }, []);

  // Build the custom form fields array for the Acuity appointment.
  // Fallback values are sent for fields the patient never filled in so the
  // Acuity form is always submitted with every required field present.
  const buildAppointmentFields = useCallback(() => {
    const fields: { id: number; value: string }[] = [];
    const acuityFields = getAcuityFieldIdsForFlow(
      getAcuityFlow(effectiveTestType)
    );

    fields.push({
      id: FIELD_IDS.dateOfBirth,
      value: acuityIntakeFormData?.dateOfBirth || "01/01/01",
    });
    fields.push({
      id: FIELD_IDS.physicianClinic,
      value: acuityIntakeFormData?.physicianClinic || "n/a",
    });
    fields.push({
      id: FIELD_IDS.orderingPhysician,
      value: acuityIntakeFormData?.orderingPhysician || "n/a",
    });
    // No-order patients have no order-backed cancer info on `intakeFormData`;
    // their answers live on the in-session `prefilledData`, so prefer that and
    // fall back to the order-backed prefill for the standard flow.
    const cancerType = acuityIntakeFormData?.cancerType;
    const cancerStage = acuityIntakeFormData?.cancerStage;
    // No-order patients can type a free-text cancer type via the "Other"
    // option; the multibox carries "Other" and the typed text goes to the
    // companion "Other cancer type" field so Acuity shows what they entered.
    // Order-backed clinical codes still collapse to a bare "Other".
    const resolvedCancerType = resolveCancerTypeAcuityValue(
      cancerType,
      isNoOrder
    );
    fields.push({
      id: FIELD_IDS.cancerType,
      value: resolvedCancerType.label ?? "Other",
    });
    if (resolvedCancerType.otherText) {
      fields.push({
        id: FIELD_IDS.cancerTypeOther,
        value: resolvedCancerType.otherText,
      });
    }
    if (cancerStage) {
      const label = CANCER_STAGE_LABELS[cancerStage];
      if (label) fields.push({ id: FIELD_IDS.cancerStage, value: label });
    }
    // Default to "yes" when the patient already has a result on file (status
    // RESULT_READY or TEST_COMPLETE implies blood was previously drawn through
    // Natera); otherwise default to "no".
    const hasPreviouslyTakenTestValue =
      acuityIntakeFormData?.hasPreviouslyTakenTest != null
        ? acuityIntakeFormData.hasPreviouslyTakenTest
        : testToUse?.status === TestCardStatus.RESULT_READY ||
          testToUse?.status === TestCardStatus.TEST_COMPLETE;
    fields.push({
      id: FIELD_IDS.hasPreviouslyTakenTest,
      value: hasPreviouslyTakenTestValue ? "yes" : "no",
    });
    if (acuityFields.caseId != null && limsCaseId != null) {
      fields.push({
        id: acuityFields.caseId,
        value: String(limsCaseId),
      });
    }
    const enteredByName = [
      acuityIntakeFormData?.firstName,
      acuityIntakeFormData?.lastName,
    ]
      .filter(Boolean)
      .join(" ");
    fields.push({ id: FIELD_IDS.enteredBy, value: enteredByName || "n/a" });

    const reasonForAppointmentDefault = REASON_LABELS.signatera;
    let reasonValue = reasonForAppointmentDefault;
    if (userInputFields?.reasonForAppointment) {
      const reasons = userInputFields.reasonForAppointment
        .split(",")
        .map((r) => r.trim())
        .map((r) => REASON_LABELS[r])
        .filter(Boolean);
      if (reasons.length) {
        reasonValue = reasons.join(", ");
      }
    }
    fields.push({
      id: FIELD_IDS.reasonForAppointment,
      value: reasonValue,
    });

    if (userInputFields) {
      if (userInputFields.hasActiveDisease != null) {
        fields.push({
          id: FIELD_IDS.hasActiveDisease,
          value:
            ACTIVE_DISEASE_LABELS[String(userInputFields.hasActiveDisease)],
        });
      }
      if (userInputFields.countryOfResidence) {
        const countryLabel =
          COUNTRIES.find((c) => c.value === userInputFields.countryOfResidence)
            ?.label ?? userInputFields.countryOfResidence;
        fields.push({
          id: FIELD_IDS.countryOfResidence,
          value: countryLabel,
        });
      }
      if (userInputFields.interpreterNeeded != null) {
        fields.push({
          id: FIELD_IDS.interpreterNeeded,
          value:
            INTERPRETER_NEEDED_LABELS[
              String(userInputFields.interpreterNeeded)
            ],
        });
      }
      if (userInputFields.interpreterLanguage) {
        const languageLabel =
          LANGUAGES.find((l) => l.value === userInputFields.interpreterLanguage)
            ?.label ?? userInputFields.interpreterLanguage;
        fields.push({
          id: FIELD_IDS.interpreterLanguage,
          value: languageLabel,
        });
      }
      if (userInputFields.consentAcknowledged) {
        fields.push({
          id: FIELD_IDS.consent.patientMustBePresent,
          value: "yes",
        });
        fields.push({ id: FIELD_IDS.consent.emailConsent, value: "yes" });
        fields.push({ id: FIELD_IDS.consent.noRecordingConsent, value: "yes" });
        fields.push({
          id: FIELD_IDS.consent.privacyPolicyConsent,
          value: "yes",
        });
      }
    }

    // Use the effective test type (not the raw order test type) so the
    // scheduling-source / counseling-intent custom-field IDs match the calendar
    // we actually book into — including the ONC default applied for no-order
    // patients, who have no test type of their own.
    const { schedulingSourceFieldId, counselingIntentFieldId } =
      getAcuityOwnerConfig(effectiveTestType);
    // Anonymous order-lookup guests always attribute to the order-lookup
    // funnel. Public GIS entry (Work Item 2) stores its marker in
    // sessionStorage when the user lands on the public landing page; everything
    // else (e.g. test result details page) keeps the legacy value so existing
    // routes don't change behavior.
    const schedulingSourceValue = isGuest
      ? GIS_ENTRY_SOURCE_ORDER_LOOKUP
      : getGisEntrySource() === GIS_ENTRY_SOURCE_PUBLIC
        ? GIS_ENTRY_SOURCE_PUBLIC
        : "testResultDetailsPage";
    fields.push({
      id: Number(schedulingSourceFieldId),
      value: schedulingSourceValue,
    });

    // Counseling intent (pre-test vs post-test) — sourced from the intake
    // form context (Work Item 4). Fall back to the test status classifier
    // so we still emit a sensible value if the user lands directly on the
    // schedule step without first hitting the intake flow.
    fields.push({
      id: Number(counselingIntentFieldId),
      value: COUNSELING_INTENT_LABELS[effectiveCounselingIntent],
    });

    return fields;
  }, [
    acuityIntakeFormData,
    userInputFields,
    effectiveTestType,
    limsCaseId,
    effectiveCounselingIntent,
    isGuest,
  ]);

  const saveIntakeSession = useCallback(async (): Promise<number | null> => {
    if (!orderUidPublic || !testUid) return null;

    const toNonNullFields = (
      fields: IntakeFormUserInputFields
    ): Record<string, unknown> => {
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(fields)) {
        if (value !== null && value !== undefined) {
          result[key] = value;
        }
      }
      return result;
    };

    try {
      // The triage row is created earlier (when the user clicks
      // "Schedule a GIS" on the test result details page, or up-front by the
      // order-lookup funnel for guests). Look it up, patch in the user's
      // intake answers, then mark it COMPLETED.
      const activeSession = await getActiveSession({ orderUidPublic, testUid });
      const sessionId = activeSession?.id;
      if (sessionId == null) {
        console.warn(
          "No active GIS intake session found to update; skipping save."
        );
        return null;
      }

      const inputFields = toNonNullFields(userInputFields);
      const hasInput = Object.keys(inputFields).length > 0;
      const hasContact = confirmedPhone != null || confirmedEmail != null;
      if (hasInput || hasContact) {
        await updateStep({
          sessionId,
          input: inputFields,
          confirmedPhone: confirmedPhone ?? undefined,
          confirmedEmail: confirmedEmail ?? undefined,
        });
      }
      await completeSession({ sessionId });
      return sessionId;
    } catch (err) {
      console.error("Failed to save GIS intake session:", err);
      return null;
    }
  }, [
    orderUidPublic,
    testUid,
    userInputFields,
    confirmedPhone,
    confirmedEmail,
    getActiveSession,
    updateStep,
    completeSession,
  ]);

  const handleSchedule = useCallback(async () => {
    if (
      !selectedDate ||
      !selectedTimeISO ||
      !appointmentTypeID ||
      !orderUidPublic ||
      !testUid ||
      !intakeFormData
    ) {
      setScheduleError(true);
      return;
    }

    const fields = buildAppointmentFields();
    setSlotError(false);
    setScheduleError(false);

    setIsScheduleSubmitInFlight(true);
    try {
      const result = await createAppointment({
        orderUidPublic,
        testUid,
        token: guestToken,
        input: {
          datetime: selectedTimeISO,
          appointmentTypeID,
          // No-order patients confirm/edit their name on the intake form; those
          // edits live on `prefilledData`, so prefer them and fall back to the
          // order-backed prefill for the standard flow.
          firstName: prefilledData?.firstName || intakeFormData.firstName || "",
          lastName: prefilledData?.lastName || intakeFormData.lastName || "",
          email: confirmedEmail || intakeFormData.email || "",
          phone: confirmedPhone || intakeFormData.phone || undefined,
          timezone,
          calendarID: selectedCalendarID ?? undefined,
          fields,
        },
      });

      if (result) {
        const sessionId = await saveIntakeSession();
        // Fire-and-forget: stamp `booking_confirmed` on the triage row to
        // record that an Acuity appointment now exists. `intake_completed`
        // is stamped server-side inside completeSession (run by
        // saveIntakeSession above), so we don't need a separate call here.
        if (sessionId != null) {
          void confirmBooking({ sessionId }).catch((err) => {
            console.error("Failed to confirm GIS booking timestamp:", err);
          });
        }
        // Lock the intake answers: once the appointment exists the patient
        // must not be able to navigate back and edit the intake form. The
        // flow's editable steps redirect away while this flag is set.
        setBookingConfirmed(true);
        setBookedAppointment(result);
        setView("confirmation");
      } else {
        setScheduleError(true);
      }
    } catch (err) {
      if (
        err instanceof ApolloError &&
        err.graphQLErrors[0]?.extensions?.code === ResultCodes.SLOT_UNAVAILABLE
      ) {
        setSlotError(true);
        if (slotErrorTimerRef.current) clearTimeout(slotErrorTimerRef.current);
        slotErrorTimerRef.current = setTimeout(() => setSlotError(false), 7000);
        const dateStr =
          DateTime.fromJSDate(selectedDate).toFormat("yyyy-MM-dd");
        fetchTimes({
          orderUidPublic,
          testUid,
          appointmentTypeID,
          date: dateStr,
          timezone,
          calendarID: selectedCalendarID ?? undefined,
          token: guestToken,
        });
        const month = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}`;
        fetchDates({
          orderUidPublic,
          testUid,
          appointmentTypeID,
          month,
          timezone,
          calendarID: selectedCalendarID ?? undefined,
          token: guestToken,
        });
        setUnavailableSlots((prev) => new Set(prev).add(selectedTimeISO));
        setSelectedTimeISO(null);
      } else {
        console.error("Failed to schedule GIS appointment:", err);
        setScheduleError(true);
      }
    } finally {
      setIsScheduleSubmitInFlight(false);
    }
  }, [
    selectedDate,
    selectedTimeISO,
    appointmentTypeID,
    orderUidPublic,
    testUid,
    intakeFormData,
    prefilledData,
    timezone,
    buildAppointmentFields,
    createAppointment,
    saveIntakeSession,
    confirmBooking,
    fetchTimes,
    fetchDates,
    selectedCalendarID,
    calendarYear,
    calendarMonth,
    confirmedEmail,
    confirmedPhone,
    guestToken,
    setBookingConfirmed,
  ]);

  useEffect(() => {
    if (intakeFormError) {
      setScheduleError(true);
    }
  }, [intakeFormError]);

  const setCounselorCalendarId = useCallback((calendarID: number | null) => {
    setSelectedCalendarID(calendarID);
  }, []);

  const handleCounselorNext = useCallback(() => {
    setSelectedCalendarID((prev) => (prev === undefined ? null : prev));
    setSelectedDate(null);
    setSelectedTimeISO(null);
    setView("dateSelection");
  }, []);

  const handleBackToCouncselors = useCallback(() => {
    setSelectedDate(null);
    setSelectedTimeISO(null);
    setView("counselorSelection");
  }, []);

  const handleBack = useCallback(() => {
    if (view === "dateSelection") {
      handleBackToCouncselors();
      return;
    }
    navigate(-1);
  }, [view, navigate, handleBackToCouncselors]);

  // Acuity answers Back with its own scheduling steps from inside the frame,
  // so Back off the WH scheduler is handed back to this page, which returns
  // the patient to whichever screen sent them here.
  const handleWhSchedulerBack = useEmbeddedFrameBack(
    Boolean(isWhFlow && whAcuityUrl)
  );

  const handleGoToResults = useCallback(() => {
    if (orderUidPublic && testUid) {
      navigate(routes.testResultDetailsPage(orderUidPublic, testUid));
      return;
    }
    navigate(routes.home);
  }, [navigate, orderUidPublic, testUid]);

  const timezoneDisplayLabel = useMemo(() => {
    const translated = getTranslatedTimeZoneUserValue(timezone, intl);
    const offset = getTimezoneOffsetLabel(timezone);
    return `${translated} (${offset})`;
  }, [timezone, intl]);

  const isDateSelected = (day: CalendarDay) =>
    selectedDate &&
    day.date.getFullYear() === selectedDate.getFullYear() &&
    day.date.getMonth() === selectedDate.getMonth() &&
    day.date.getDate() === selectedDate.getDate();

  if (isLoading) {
    return (
      <div className="schedule-session-page">
        <div className="schedule-session-page__loader">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  // A booking already exists for this order/test (e.g. the patient navigated
  // back to this route after confirming, losing the in-memory confirmation
  // state). Don't re-render the selectable counselor/date/time views — that
  // would let them book a second appointment. Send them to a terminal page
  // instead. `bookedAppointment` is still set on the fresh just-booked render,
  // so the confirmation view below continues to show normally.
  if (bookingConfirmed && !bookedAppointment) {
    // Guests have no authenticated portal to return to, so send them back to
    // the public GIS entry page rather than a login-gated route.
    const target = isGuest
      ? routes.publicGisEntry
      : isPostTest && orderUidPublic && testUid
        ? routes.testResultDetailsPage(orderUidPublic, testUid)
        : routes.home;
    return <Navigate to={target} replace />;
  }

  // ---------- WH (Empower) iframe view ----------
  // The WH/Empower scheduler runs entirely inside Acuity's hosted scheduler;
  // we don't drive the calendar/dates/times ourselves. Booking confirmation
  // happens in the iframe and via Acuity's confirmation email.
  if (isWhFlow && whAcuityUrl) {
    return (
      <div className="schedule-session-page">
        <div className="schedule-session-page__card-container schedule-session-page__card-container--wide">
          <div className="schedule-session-page__card">
            <h2 className="schedule-session-page__title">
              {intl.formatMessage(messages.whSchedulerTitle)}
            </h2>
            <iframe
              src={whAcuityUrl}
              className="schedule-session-page__iframe"
              title={intl.formatMessage(messages.whSchedulerIframeLabel)}
            />
            <div className="schedule-actions">
              <button
                type="button"
                className="schedule-actions__back"
                onClick={handleWhSchedulerBack}
              >
                {intl.formatMessage(messages.backButton)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Confirmation view ----------
  if (view === "confirmation" && bookedAppointment) {
    const appt = bookedAppointment;
    return (
      <div className="schedule-session-page">
        <div className="schedule-session-page__card-container">
          <div className="schedule-session-page__card">
            <div className="schedule-confirmation">
              <div className="schedule-confirmation__header">
                <div className="schedule-confirmation__icon">
                  <Svg content={CheckmarkSuccess} />
                </div>
                <h2 className="schedule-confirmation__title">
                  {intl.formatMessage(messages.confirmationTitle)}
                </h2>
                <p className="schedule-confirmation__subtitle">
                  {intl.formatMessage(messages.confirmationSubtitle)}
                </p>
              </div>

              <div className="schedule-confirmation__summary">
                <h3 className="schedule-confirmation__summary-title">
                  {intl.formatMessage(messages.summaryTitle)}
                </h3>
                <div className="schedule-confirmation__metadata">
                  <div className="schedule-confirmation__metadata-row">
                    <span className="schedule-confirmation__metadata-label">
                      {intl.formatMessage(messages.dateLabel)}
                    </span>
                    <span className="schedule-confirmation__metadata-value">
                      {formatAppointmentDateInZone(
                        appt.datetime,
                        timezone,
                        intl
                      )}
                    </span>
                  </div>
                  <div className="schedule-confirmation__metadata-row">
                    <span className="schedule-confirmation__metadata-label">
                      {intl.formatMessage(messages.timeLabel)}
                    </span>
                    <span className="schedule-confirmation__metadata-value">
                      {formatTimeFromISO(appt.datetime, timezone)}
                    </span>
                  </div>
                  <div className="schedule-confirmation__metadata-row">
                    <span className="schedule-confirmation__metadata-label">
                      {intl.formatMessage(messages.timezoneLabel)}
                    </span>
                    <span className="schedule-confirmation__metadata-value">
                      {timezoneDisplayLabel}
                    </span>
                  </div>
                  <div className="schedule-confirmation__metadata-row">
                    <span className="schedule-confirmation__metadata-label">
                      {intl.formatMessage(messages.durationLabel)}
                    </span>
                    <span className="schedule-confirmation__metadata-value">
                      {appt.duration
                        ? `${appt.duration} minutes`
                        : intl.formatMessage(messages.durationValue)}
                    </span>
                  </div>
                </div>

                <p className="schedule-confirmation__calendar-section-title">
                  {intl.formatMessage(messages.addToCalendar)}
                </p>
                <div className="schedule-confirmation__calendar-buttons">
                  <button
                    type="button"
                    className="schedule-confirmation__calendar-btn"
                    onClick={() => {
                      const dt = DateTime.fromISO(appt.datetime).setZone(
                        timezone
                      );
                      const endDt = dt.plus({
                        minutes: parseInt(appt.duration, 10) || 30,
                      });
                      const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(appt.type)}&dates=${dt.toFormat("yyyyMMdd'T'HHmmss")}/${endDt.toFormat("yyyyMMdd'T'HHmmss")}&ctz=${encodeURIComponent(timezone)}`;
                      window.open(gcalUrl, "_blank");
                    }}
                  >
                    <img
                      src={GoogleCalendarLogo}
                      alt=""
                      className="schedule-confirmation__calendar-btn-icon"
                    />
                    {intl.formatMessage(messages.google)}
                  </button>
                  <button
                    type="button"
                    className="schedule-confirmation__calendar-btn"
                    onClick={() => {
                      const dt = DateTime.fromISO(appt.datetime).toUTC();
                      const endDt = dt.plus({
                        minutes: parseInt(appt.duration, 10) || 30,
                      });
                      const icsContent = [
                        "BEGIN:VCALENDAR",
                        "VERSION:2.0",
                        "BEGIN:VEVENT",
                        `DTSTART:${dt.toFormat("yyyyMMdd'T'HHmmss'Z'")}`,
                        `DTEND:${endDt.toFormat("yyyyMMdd'T'HHmmss'Z'")}`,
                        `SUMMARY:${appt.type}`,
                        "END:VEVENT",
                        "END:VCALENDAR",
                      ].join("\r\n");
                      const blob = new Blob([icsContent], {
                        type: "text/calendar",
                      });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "appointment.ics";
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Svg
                      content={AppleLogo}
                      className="schedule-confirmation__calendar-btn-icon"
                    />
                    {intl.formatMessage(messages.apple)}
                  </button>
                </div>

                <div className="schedule-confirmation__divider" />

                <h3 className="schedule-confirmation__expect-title">
                  {intl.formatMessage(messages.expectTitle)}
                </h3>
                <p className="schedule-confirmation__expect-text">
                  {intl.formatMessage(messages.expectBody)}
                </p>
              </div>

              {isPostTest && !isGuest && (
                <div className="schedule-actions">
                  <Button
                    className="schedule-actions__schedule"
                    unelevated
                    onClick={handleGoToResults}
                  >
                    {intl.formatMessage(messages.goToResults)}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Counselor selection view ----------
  if (view === "counselorSelection") {
    const anyCounselorSelected =
      selectedCalendarID === undefined || selectedCalendarID === null;

    return (
      <div className="schedule-session-page">
        <div className="schedule-session-page__card-container">
          <div className="schedule-session-page__card schedule-session-page__card--counselor">
            <h2 className="schedule-session-page__title">
              {intl.formatMessage(messages.selectCounselorTitle)}
            </h2>

            <div className="schedule-counselor">
              <div className="schedule-counselor__appointment">
                <h3 className="schedule-counselor__appointment-label">
                  {intl.formatMessage(messages.appointmentLabel)}
                </h3>
                <p className="schedule-counselor__appointment-type">
                  {appointmentTypeName}
                </p>
                <p className="schedule-counselor__appointment-duration">
                  {intl.formatMessage(messages.appointmentDurationShort)}
                </p>
              </div>

              <div
                className="schedule-counselor__list-scroll"
                role="radiogroup"
                aria-label={intl.formatMessage(messages.selectCounselorTitle)}
              >
                {calendarsLoading ? (
                  <div className="schedule-counselor__list-loading">
                    <SpinnerLoader />
                  </div>
                ) : (
                  <div className="schedule-counselor__list-inner">
                    <label
                      className={
                        anyCounselorSelected
                          ? "schedule-counselor__row schedule-counselor__row--any schedule-counselor__row--selected"
                          : "schedule-counselor__row schedule-counselor__row--any"
                      }
                    >
                      <input
                        type="radio"
                        className="schedule-counselor__radio"
                        name="gis-counselor"
                        checked={anyCounselorSelected}
                        onChange={() => setCounselorCalendarId(null)}
                      />
                      <span className="schedule-counselor__row-main">
                        <span className="schedule-counselor__row-name schedule-counselor__row-name--action-dark">
                          {intl.formatMessage(messages.anyAvailable)}
                        </span>
                        <span className="schedule-counselor__row-earliest">
                          <Svg
                            content={Bolt}
                            className="schedule-counselor__bolt"
                          />
                          <span>
                            {intl.formatMessage(messages.earliestAvailability)}
                          </span>
                        </span>
                      </span>
                    </label>

                    {calendars.map((cal) => {
                      const selected = selectedCalendarID === cal.id;
                      return (
                        <label
                          key={cal.id}
                          className={
                            selected
                              ? "schedule-counselor__row schedule-counselor__row--counselor schedule-counselor__row--selected"
                              : "schedule-counselor__row schedule-counselor__row--counselor"
                          }
                        >
                          <input
                            type="radio"
                            className="schedule-counselor__radio"
                            name="gis-counselor"
                            checked={selected}
                            onChange={() => setCounselorCalendarId(cal.id)}
                          />
                          <span className="schedule-counselor__row-name">
                            {cal.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="schedule-actions">
              <Button
                className="schedule-actions__schedule"
                unelevated
                onClick={handleCounselorNext}
                disabled={calendarsLoading}
              >
                {intl.formatMessage(messages.nextButton)}
              </Button>
              <button
                type="button"
                className="schedule-actions__back"
                onClick={() => navigate(-1)}
              >
                {intl.formatMessage(messages.backButton)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Date / time selection view ----------
  return (
    <div className="schedule-session-page">
      <div className="schedule-session-page__card-container">
        <div className="schedule-session-page__card">
          <h2 className="schedule-session-page__title">
            {intl.formatMessage(messages.title)}
          </h2>

          {/* Calendar */}
          <div className="schedule-calendar">
            <div className="schedule-calendar__nav">
              <select
                className="schedule-calendar__month-select"
                value={calendarMonth}
                onChange={handleMonthChange}
                aria-label="Select month"
              >
                {monthOptions.map(({ value, label }) => (
                  <option key={value} value={value - 1}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className="schedule-calendar__year-select"
                value={calendarYear}
                onChange={handleYearChange}
                aria-label="Select year"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="schedule-calendar__nav-btn"
                onClick={handlePrevMonth}
                aria-label="Previous month"
              >
                &#8592;
              </button>
              <button
                type="button"
                className="schedule-calendar__nav-btn"
                onClick={handleNextMonth}
                aria-label="Next month"
              >
                &#8594;
              </button>
            </div>

            <div className="schedule-calendar__grid" role="grid">
              {weekdayLabels.map((day, i) => (
                <span
                  key={i}
                  className="schedule-calendar__weekday"
                  role="columnheader"
                >
                  {day}
                </span>
              ))}
              {calendarDays.map((day, idx) => {
                const selected = isDateSelected(day);
                const classNames = [
                  "schedule-calendar__day",
                  !day.isCurrentMonth && "schedule-calendar__day--other-month",
                  day.isAvailable &&
                    !selected &&
                    "schedule-calendar__day--available",
                  selected && "schedule-calendar__day--selected",
                  day.isToday && "schedule-calendar__day--today",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <button
                    key={idx}
                    type="button"
                    className={classNames}
                    onClick={() => handleDayClick(day)}
                    disabled={!day.isAvailable}
                    aria-label={`${monthOptions[day.date.getMonth()]?.label ?? ""} ${day.dayOfMonth}`}
                    aria-selected={selected || undefined}
                    tabIndex={day.isAvailable ? 0 : -1}
                    role="gridcell"
                  >
                    {day.dayOfMonth}
                  </button>
                );
              })}
            </div>

            <div className="schedule-calendar__divider" />
          </div>

          {/* Timezone selector */}
          {selectedDate && (
            <div className="schedule-timezone">
              <ErrorProvider controller={errorController}>
                <TimezoneSelector
                  value={timezone}
                  onSelect={setTimezone}
                  variant="filled"
                  showGmtOffset
                  hideLabel
                />
              </ErrorProvider>
            </div>
          )}

          {/* Time slots */}
          {selectedDate && (
            <div className="schedule-timeslots">
              {timesLoading ? (
                <div className="schedule-session-page__loader">
                  <SpinnerLoader />
                </div>
              ) : filteredTimesList.length > 0 ? (
                <div className="schedule-timeslots__grid">
                  {filteredTimesList.map((slot) => (
                    <button
                      key={slot.time}
                      type="button"
                      className={`schedule-timeslots__chip${
                        selectedTimeISO === slot.time
                          ? " schedule-timeslots__chip--selected"
                          : ""
                      }`}
                      onClick={() => {
                        setSlotError(false);
                        setSelectedTimeISO(slot.time);
                      }}
                      aria-pressed={selectedTimeISO === slot.time}
                    >
                      {selectedTimeISO === slot.time && (
                        <span className="schedule-timeslots__chip-icon">
                          <Svg content={Checkmark} />
                        </span>
                      )}
                      {formatTimeFromISO(slot.time, timezone)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="schedule-timeslots__empty">
                  {intl.formatMessage(messages.noSessions)}
                </p>
              )}
            </div>
          )}

          {/* No-date-selected message */}
          {!selectedDate && !datesLoading && (
            <div className="schedule-timeslots">
              <p className="schedule-timeslots__empty">
                {intl.formatMessage(messages.noSessions)}
              </p>
            </div>
          )}

          {datesLoading && !selectedDate && (
            <div className="schedule-session-page__loader">
              <SpinnerLoader />
            </div>
          )}

          {/* Slot unavailable alert */}
          {slotError && (
            <div className="schedule-slot-error" role="alert">
              <Svg content={ErrorIcon} />
              <p className="schedule-slot-error__message">
                {intl.formatMessage(messages.slotUnavailable)}
              </p>
              <button
                type="button"
                className="schedule-slot-error__close"
                onClick={() => setSlotError(false)}
                aria-label="Close"
              >
                <Svg content={CloseIcon} />
              </button>
            </div>
          )}

          {scheduleError && !slotError && (
            <div className="schedule-slot-error" role="alert">
              <Svg content={ErrorIcon} />
              <p className="schedule-slot-error__message">
                {intl.formatMessage(messages.scheduleError)}
              </p>
              <button
                type="button"
                className="schedule-slot-error__close"
                onClick={() => setScheduleError(false)}
                aria-label="Close"
              >
                <Svg content={CloseIcon} />
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="schedule-actions">
            <Button
              className={`schedule-actions__schedule${
                isScheduleSubmitInFlight
                  ? " schedule-actions__schedule--loading"
                  : ""
              }`}
              unelevated
              onClick={handleSchedule}
              disabled={
                !selectedDate ||
                !selectedTimeISO ||
                !appointmentTypeID ||
                !orderUidPublic ||
                !testUid ||
                !intakeFormData ||
                isScheduleSubmitInFlight
              }
              aria-busy={isScheduleSubmitInFlight}
            >
              {isScheduleSubmitInFlight ? (
                <span className="schedule-actions__schedule-content schedule-actions__schedule-content--loading-only">
                  <span className="schedule-actions__schedule-spinner">
                    <SpinnerLoader />
                  </span>
                </span>
              ) : (
                intl.formatMessage(messages.scheduleButton)
              )}
            </Button>
            <button
              type="button"
              className="schedule-actions__back"
              onClick={handleBack}
            >
              {intl.formatMessage(messages.backButton)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
